// Applications — review fighter + doctor applications. Approving a fighter activates a $0
// subscription; approving a doctor provisions a Healthie provider (createOrganizationMembership) +
// OIDC SSO, then notifies the applicant. The orchestration runs server-side (single command).
import { useState } from "react";
import { Btn, Surface } from "@championcare/ui";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { cap, formatDateTime } from "../lib/format";
import { Loading, ErrorState, EmptyState, PageHeader, StatusBadge } from "../components/ui";

export function Applications() {
  const api = useApi();
  const { data, loading, error, reload } = useAsync(() => api.applications(), []);
  const [busy, setBusy] = useState<string | null>(null);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;
  const rows = data ?? [];

  async function act(id: string, action: "approve" | "reject") {
    setBusy(id + action);
    try {
      if (action === "approve") await api.approveApplication(id);
      else await api.rejectApplication(id);
      reload();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <PageHeader title="Applications" sub="Fighter + provider review." />
      <Surface className="card">
        {rows.length ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>Type</th>
                <th>Applicant</th>
                <th>Submitted</th>
                <th>Status</th>
                <th className="tbl__actions">Review</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const pending = a.status === "submitted" || a.status === "in_review";
                return (
                  <tr key={a.id}>
                    <td>{cap(a.kind)}</td>
                    <td>{a.applicant_email}</td>
                    <td className="mono">{formatDateTime(a.created_at)}</td>
                    <td>
                      <StatusBadge value={a.status} />
                    </td>
                    <td className="tbl__actions">
                      {pending ? (
                        <div className="row-actions">
                          <Btn
                            as="button"
                            variant="primary"
                            disabled={busy !== null}
                            onClick={() => act(a.id, "approve")}
                          >
                            {busy === a.id + "approve" ? "…" : "Approve"}
                          </Btn>
                          <Btn
                            as="button"
                            variant="ghost"
                            disabled={busy !== null}
                            onClick={() => act(a.id, "reject")}
                          >
                            Reject
                          </Btn>
                        </div>
                      ) : (
                        <span className="muted">Reviewed</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <EmptyState>No applications.</EmptyState>
        )}
      </Surface>
    </div>
  );
}
